import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PublishVersionInput, Version, VersionWithChanges, CreateVersionInput } from "@/features/versions/types";
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { versionService } from '@/services/versions';
import type { VersionChange } from '@/features/versions/types';
import { BusinessError } from '@/features/channels/utils/errors';
import { CreateVersionWithRegionInput } from '@/features/regions/types';
import type { DuplicateChannelInfo } from '@/types/duplicateChannels';
import { useVersionStore } from '@/stores/versionStore';
// Version queries
const versionQueries = {
  getVersions: async (providerId: number) => {
    const { data, error } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Version[];
  },

  getVersionById: async (id: number) => {
    const { data, error } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Version;
  },

  getDraftVersion: async (providerId: number) => {
    const { data, error } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('provider_id', providerId)
      .eq('status', 'draft')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data as Version | null;
  },

  getVersionWithChanges: async (versionId: number) => {
    const { data, error } = await supabase
      .from('provider_versions')
      .select(`
        *,
        changes:change_log(*)
      `)
      .eq('id', versionId)
      .single();

    if (error) throw error;
    return data as unknown as VersionWithChanges;
  },

  getVersionChanges: async (versionId: number) => {
    const { data, error } = await supabase
      .from('change_log')
      .select('*')
      .eq('version_id', versionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as unknown as VersionChange[];
  },

  getChangeDetails: async (changeId: number) => {
    const { data, error } = await supabase
      .from('change_log')
      .select(`
        *,
        version:provider_versions(*)
      `)
      .eq('id', changeId)
      .single();

    if (error) throw error;
    return data;
  },

  getVersionsByRegion: async (providerId: number, regionId: number) => {
    const { data, error } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('provider_id', providerId)
      .eq('region_id', regionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Version[];
  }
};

// Version mutations
const versionMutations = {
  createVersion: async (data: CreateVersionInput) => {
    // 1. Check for existing draft
    const { data: existingDraft } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('provider_id', data.provider_id)
      .eq('status', 'draft')
      .single();

    if (existingDraft) {
      throw new Error('A draft version already exists');
    }

    // 2. Get latest published version
    const { data: latestPublished } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('provider_id', data.provider_id)
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    // 3. Get next version number
    const { data: nextVersion } = await supabase
      .rpc('get_next_version_number', {
        provider_id_param: data.provider_id
      });

    // 4. Create new draft version
    const { data: newVersion, error: createError } = await supabase
      .from('provider_versions')
      .insert({
        provider_id: data.provider_id,
        region_id: 1, // Default to region 1
        version_number: nextVersion || '1.0',
        status: 'draft',
        description: data.description || '',
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (createError) throw createError;

    // 5. Copy data from latest published version if exists
    if (latestPublished) {
      const { error: copyError } = await supabase
        .rpc('copy_version_data', {
          source_version_id: latestPublished.id,
          target_version_id: newVersion.id
        });

      if (copyError) throw copyError;
    }

    return newVersion as Version;
  },

  publishVersion: async (id: number, data: PublishVersionInput) => {
    // 1. First update the version with effective date and description
    const { error: updateError } = await supabase
      .from('provider_versions')
      .update({
        effective_date: data.effective_date,
        description: data.description || ''
      })
      .eq('id', id)
      .eq('status', 'draft');

    if (updateError) throw updateError;

    // 2. Check if version can be published
    const { data: canPublish, error: checkError } = await supabase
      .rpc('can_publish_version', { 
        version_id_param: id 
      });

    if (checkError) throw checkError;
    if (!canPublish) {
      throw new Error('Please ensure all required data is set before publishing');
    }

    // 3. Publish the version
    const { data: version, error } = await supabase
      .from('provider_versions')
      .update({
        status: 'published',
        published_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('status', 'draft')
      .select('*')
      .single();

    if (error) throw error;
    return version as Version;
  }
};

export function useVersions(providerId: number, regionId?: number) {
  return useQuery({
    queryKey: regionId ? ['versions', providerId, 'region', regionId] : ['versions', providerId],
    queryFn: () => regionId 
      ? versionQueries.getVersionsByRegion(providerId, regionId)
      : versionQueries.getVersions(providerId),
    enabled: !!providerId
  });
}

export function useVersion(params: { providerId: number; versionId: number }) {
  return useQuery<Version>({
    queryKey: ['version', params.providerId, params.versionId],
    queryFn: () => versionQueries.getVersionById(params.versionId),
  });
}

export function useVersionWithChanges(versionId: number) {
  return useQuery<VersionWithChanges>({
    queryKey: ['version-changes', versionId],
    queryFn: () => versionQueries.getVersionWithChanges(versionId),
  });
}

export function useDraftVersion(providerId: number) {
  return useQuery<Version | null>({
    queryKey: ['versions', providerId, 'draft'],
    queryFn: () => versionQueries.getDraftVersion(providerId),
  });
}

// Helper function to parse duplicate channel errors from RPC functions
function parseDuplicateChannelError(error: any): DuplicateChannelInfo[] | null {
  try {
    // Check if this is a duplicate channel error from the RPC function
    if (error?.message && error.message.includes('is already in use by another non-local, non-duplicate channel')) {
      console.log('Duplicate channel error detected:', error);
      
      // Parse the error message to extract channel information
      // Error format: "Channel number 121 (type_id: 1550) is already in use by another non-local, non-duplicate channel in version 1301 (found 1 existing)"
      const regex = /Channel number (\d+) \(type_id: (\d+)\) is already in use by another non-local, non-duplicate channel in version (\d+)/;
      const match = error.message.match(regex);
      console.log('Regex match result:', match);
      
      if (match) {
        const [, channelNumber, typeId] = match;
        
        // Create channel info from the error data
        // We need to find the channel that has this number in the current published version
        const duplicateChannels: DuplicateChannelInfo[] = [
          {
            id: parseInt(typeId), // Using type_id temporarily
            name: `Channel ${channelNumber}`,
            channel_numbers: [channelNumber],
            category_name: null, // Will be fetched if needed
            is_local: false,
            is_duplicate: false
          }
        ];
        
        return duplicateChannels;
      }
    }
    
    return null;
  } catch (parseError) {
    console.error('Error parsing duplicate channel error:', parseError);
    return null;
  }
}

export function useCreateVersion() {
  const queryClient = useQueryClient();
  const { openDuplicateChannelModal } = useVersionStore();
  
  return useMutation({
    mutationFn: async (data: CreateVersionInput | CreateVersionWithRegionInput) => {
      try {
        // Check if region_id is present but could be null/undefined
        const hasRegionKey = 'region_id' in data;
        const hasValidRegion = hasRegionKey && data.region_id !== null && data.region_id !== undefined;
        
        // Make sure we only pass region_id if it's a valid value
        if (hasRegionKey && !hasValidRegion) {
          // If region_id is explicitly null/undefined, create a clean copy without it
          const { region_id, ...cleanData } = data as CreateVersionWithRegionInput;
          return await versionService.createVersion(data.provider_id, cleanData as CreateVersionInput);
        }
        
        return await versionService.createVersion(data.provider_id, data);
      } catch (error: any) {
        // Check if this is a duplicate channel error from the RPC function
        console.log('Checking error:', error);
        console.log('Error code:', error?.code);
        console.log('Error message includes check:', error?.message?.includes('is already in use by another non-local, non-duplicate channel'));
        
        if (error?.code === 'P0001' && error?.message?.includes('is already in use by another non-local, non-duplicate channel')) {
          console.log('Duplicate channel error condition met!');
          
          // Try to extract channel information from error details
          try {
            // Parse the error to extract duplicate channel information
            const duplicateChannelInfo = parseDuplicateChannelError(error);
            console.log('Parsed duplicate channel info:', duplicateChannelInfo);
            
            if (duplicateChannelInfo && duplicateChannelInfo.length > 0) {
              console.log('About to show toast and open modal');
              // Show toast notification about duplicate channels
              toast.error(`${duplicateChannelInfo.length} channels need resolution before version creation can continue.`);
              
              // Open the duplicate channel resolution modal
              console.log('Calling openDuplicateChannelModal with:', duplicateChannelInfo, data);
              openDuplicateChannelModal(duplicateChannelInfo, data);
              
              // Don't throw the error - the modal will handle the resolution
              // Return a special value to indicate this was handled
              console.log('Returning null to indicate handled');
              return null;
            } else {
              console.log('No duplicate channel info parsed or empty array');
            }
          } catch (parseError) {
            console.error('Error parsing duplicate channel information:', parseError);
          }
        } else {
          console.log('Not a duplicate channel error, will rethrow');
        }
        
        // For all other errors, rethrow to trigger onError
        throw error;
      }
    },
    onSuccess: (data) => {
      // Don't show success for null responses (duplicate channel cases)
      if (data === null) return;
      
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      if (data?.provider_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['versions', data.provider_id] 
        });
      }
      
      // Also invalidate region-specific queries if applicable
      if (data?.region_id && data?.provider_id) {
        queryClient.invalidateQueries({
          queryKey: ['versions', data.provider_id, 'region', data.region_id]
        });
      }
      
      toast.success(`Draft version ${data?.version_number || 'new'} created successfully`);
    },
    onError: (error: any) => {
      let message = 'Failed to create version';
      
      if (error instanceof BusinessError) {
        message = error.message;
      } else if (error?.message) {
        message = error.message;
      }

      // Show regular error toast for non-duplicate-channel errors
      toast.error(message);
    }
  });
}

export function usePublishVersion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PublishVersionInput }) => {
      return await versionMutations.publishVersion(id, data);
    },
    onSuccess: (data: Version) => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      queryClient.invalidateQueries({ queryKey: ['version', data.id] });
      queryClient.invalidateQueries({ queryKey: ['version-changes'] });
      
      toast.success(`Version ${data.version_number} published successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useDeleteVersion(providerId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ versionId, force }: { versionId: number; force: boolean }) => 
      versionService.deleteVersion(versionId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', providerId] });
      toast.success('Version deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useUpdateVersion(providerId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Version> }) => {
      return await versionService.updateVersion(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', providerId] });
      toast.success('Version updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useVersionChanges(versionId: number) {
  return useQuery({
    queryKey: ['version-changes', versionId],
    queryFn: () => versionQueries.getVersionChanges(versionId),
    enabled: !!versionId
  });
}

export function useChangeDetails(changeId?: number) {
  return useQuery({
    queryKey: ['change-details', changeId],
    queryFn: async () => {
      if (!changeId) return null;

      const { data, error } = await supabase
        .from('change_log')
        .select(`
          *,
          version:provider_versions(*)
        `)
        .eq('id', changeId)
        .single();

      if (error) throw error;
      return data as unknown as VersionChange;
    },
    enabled: !!changeId
  });
}

export function useVersionStatus(providerId?: number, versionId?: number) {
  return useQuery({
    queryKey: ['version-status', providerId, versionId],
    queryFn: async () => {
      if (!providerId || !versionId) {
        throw new BusinessError('Invalid provider or version ID');
      }

      const { data, error } = await supabase
        .from('provider_versions')
        .select('status')
        .eq('provider_id', providerId)
        .eq('id', versionId)
        .single();

      if (error) {
        throw new BusinessError('Failed to check version status');
      }

      return {
        isPublished: data?.status === 'published',
        isDraft: data?.status === 'draft'
      };
    },
    enabled: Boolean(providerId && versionId)
  });
}

export function useValidateVersion() {
  return useMutation({
    mutationFn: async ({ providerId, versionId }: { providerId: number, versionId: number }) => {
      const { data, error } = await supabase
        .from('provider_versions')
        .select('status')
        .eq('provider_id', providerId)
        .eq('id', versionId)
        .single();

      if (error) {
        throw new BusinessError('Failed to validate version');
      }

      if (data?.status === 'published') {
        throw new BusinessError('Cannot modify published version');
      }

      return data;
    }
  });
}

